import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ROUTES } from '@/shared/config'
import licenseExample from '@/assets/license.png'

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

export function RegisterPage() {
  const [step, setStep] = useState<1 | 2>(1)

  // Step 1
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Step 2
  const [licenseFile, setLicenseFile] = useState<File | null>(null)
  const [licensePreview, setLicensePreview] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState('')

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    setError('')
    setStep(2)
  }

  const processFile = useCallback((file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Please upload a PNG, JPEG, WebP, or PDF file')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be under 10 MB')
      return
    }
    setError('')
    setLicenseFile(file)

    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (ev) => setLicensePreview(ev.target?.result as string)
      reader.readAsDataURL(file)
    } else {
      setLicensePreview(null)
    }
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) processFile(file)
    },
    [processFile],
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleStep2 = (e: React.FormEvent) => {
    e.preventDefault()
    if (!licenseFile) {
      setError('Please upload your broker license')
      return
    }
    setError('')
    // TODO: submit registration
  }

  const removeLicense = () => {
    setLicenseFile(null)
    setLicensePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  /* ── Step indicators ── */
  const stepIndicator = (
    <div className="flex items-center justify-center gap-3 mb-8">
      {[1, 2].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => {
            if (s === 1) { setStep(1); setError('') }
          }}
          className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-colors ${
            step === s
              ? 'text-amber-400'
              : step > s
                ? 'text-emerald-400 cursor-pointer'
                : 'text-gray-600 cursor-default'
          }`}
        >
          <span
            className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-extrabold border transition-colors ${
              step === s
                ? 'border-amber-500/60 bg-amber-500/15 text-amber-400'
                : step > s
                  ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-400'
                  : 'border-white/10 bg-white/5 text-gray-600'
            }`}
          >
            {step > s ? '✓' : s}
          </span>
          {s === 1 ? 'Account' : 'License'}
        </button>
      ))}
    </div>
  )

  return (
    <div className="flex items-center justify-center min-h-[calc(100dvh-8rem)]">
      <div className="card w-full max-w-md p-8">
        <div className="text-center mb-2">
          <h1 className="text-2xl font-extrabold text-white">Create Account</h1>
          <p className="text-sm text-gray-500 mt-2 mb-6">
            {step === 1
              ? 'Start trading with FinGrowInvest today'
              : 'Upload your broker license to continue'}
          </p>
        </div>

        {stepIndicator}

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <form onSubmit={handleStep1} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full bg-surface-800 border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-surface-800 border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-surface-800 border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-surface-800 border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm py-3 rounded-lg transition-colors"
            >
              Continue
            </button>
          </form>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <form onSubmit={handleStep2} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            {/* Upload zone */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Broker License
              </label>

              {!licenseFile ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-4 py-10 cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-amber-500/60 bg-amber-500/5'
                      : 'border-white/[0.08] bg-surface-800 hover:border-amber-500/30'
                  }`}
                >
                  <svg
                    className="w-10 h-10 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <p className="text-sm text-gray-400">
                    <span className="font-bold text-amber-400">Click to upload</span> or drag &amp; drop
                  </p>
                  <p className="text-xs text-gray-600">PNG, JPEG, WebP or PDF — max 10 MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="relative rounded-lg border border-white/[0.08] bg-surface-800 p-4">
                  {licensePreview && (
                    <img
                      src={licensePreview}
                      alt="License preview"
                      className="w-full rounded-md mb-3 max-h-52 object-contain"
                    />
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <svg className="w-5 h-5 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                      <span className="text-sm text-white truncate">{licenseFile.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeLicense}
                      className="text-gray-500 hover:text-red-400 transition-colors ml-3"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Example */}
            <details className="group">
              <summary className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-500 uppercase tracking-wider select-none">
                <svg
                  className="w-4 h-4 transition-transform group-open:rotate-90"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
                View license example
              </summary>
              <div className="mt-3 rounded-lg border border-white/[0.08] bg-surface-800 p-3">
                <img
                  src={licenseExample}
                  alt="License example"
                  className="w-full rounded-md object-contain"
                />
              </div>
            </details>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setError('') }}
                className="flex-1 border border-white/[0.08] hover:border-white/20 text-white font-bold text-sm py-3 rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-[2] bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm py-3 rounded-lg transition-colors"
              >
                Create Account
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to={ROUTES.LOGIN} className="text-amber-400 font-bold hover:text-amber-300 transition-colors">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  )
}
